
import { computed, defineComponent, nextTick, onMounted, PropType, reactive, ref, toRefs, watch } from 'vue'
import { ProductListHandlers } from '@/pages/Manage/Catalogues/composables/useProductListState'
import {
  CtCatalogue,
  CtCatalogueProduct,
  CtProduct,
  CtStockItem
} from '@/models/interfaces/content-type'
import { useStore } from 'vuex'
import { cloneDeep, debounce } from 'lodash'
import { notEmpty, uuid } from '@/utils'
import { SkuController, SkuCtrl } from '@/pages/Manage/Catalogues/utils'
import { Entry, UUID } from '@Protium/vue-ozone-utils'
import { ContentType, DirectrEvent } from '@/services/directr'
import FilterableList from '@/pages/Channels/components/FilterableList.vue'
import ProductCard from '@/pages/Channels/components/ProductCard.vue'
import ProductCardVariant from '@/pages/Channels/components/ProductCardVariant.vue'
import ChevronDownIcon from '@/components/Icons/ChevronDownIcon.vue'
import ChevronUpIcon from '@/components/Icons/ChevronUpIcon.vue'
import { useQuasar } from 'quasar'
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'
import AddProductsDialog from '@/pages/Channels/components/AddProductsDialog.vue'
import AddVariantsDialog from '@/pages/Channels/components/AddVariantsDialog.vue'
import LinkProductDialog from '@/pages/Channels/components/LinkProductDialog.vue'
import NoDataImg from '@/components/NoDataImg.vue'

interface channelDictionary {
  products: Record<UUID, CtProduct>
}

export default defineComponent({
  name: 'ChannelProducts',
  components: { ChevronUpIcon, ChevronDownIcon, ProductCardVariant, FilterableList, ProductCard, AddProductsDialog, AddVariantsDialog, LinkProductDialog, NoDataImg },
  props: {
    linkType: {
      type: String as PropType<ContentType>,
      default: ContentType.Product
    },
    catalogue: {
      type: Object as PropType<CtCatalogue>,
      required: true,
    },
    readonly: {
      type: Boolean
    }
  },
  emits: ['packing-resolved'],
  setup (props, { emit }) {
    //
    // setup
    //
    const $store = useStore()
    const $quasar = useQuasar()
    const $directr = $store.getters['Services/Directr']
    const services = ref<{ id: string, name: string, type: string, active: boolean, account?: { value: string, label: string }, [key: string]: any }[]>([])
    const $sys = $store.getters['Services/Sys']
    //
    // data
    //
    const catProdDiffState = reactive<Record<string, CtCatalogueProduct[]>>({
      lhs: [],
      rhs: [],
    })

    const products = ref<CtProduct[]>([])
    const dictionary = reactive<channelDictionary>({
      products: {} //products by sys id
    })

    const variants = ref<Record<string, string[]>>({})
    const catalogueVariants = computed(() => {
      const prodIds = catProdDiffState.rhs.map(p => p.id).filter(Boolean).map(id => id.toString())
      return Object.entries(variants.value)
        .reduce((prev, [prodId, prodVariants]) => (
          {
            ...prev,
            [prodId]: prodVariants.filter(vId => prodIds.includes(vId)).map(vId => catProdDiffState.rhs.find(catProd => catProd.id === vId))
          }
        ), {})
    })

    onMounted(async () => {
      services.value = (await $sys.get('/service'))
      $directr.listen(DirectrEvent['Update/Product'], (event) => {
        if (!event?.payload) return
        const productIndex = products.value?.findIndex(p => p.sys.id === event?.payload?.sys?.id)
        if (productIndex < 0) return
        else if (event?.payload?.sys?.id
          && products.value[productIndex].sys.updated_at
          && (new Date(event?.payload.sys.updated_at).valueOf() > new Date(products.value[productIndex].sys.updated_at as any).valueOf())
        ) products.value.splice(productIndex, 1, { ...products.value[productIndex], ...event?.payload })
      })

      $directr.listen(DirectrEvent['Create/CatalogueProduct'], (event) => {
        if (!event?.payload) return
        const productIndex = catProdDiffState.rhs?.findIndex(p => p.sys.id === event?.payload?.sys?.id)
        if (productIndex < 0) catProdDiffState.rhs.unshift(event?.payload)
      })
      $directr.listen(DirectrEvent['Update/CatalogueProduct'], (event) => {
        if (!event?.payload) return
        const productIndex = catProdDiffState.rhs?.findIndex(p => p.sys.id === event?.payload?.sys?.id)
        if (productIndex < 0) return
        else if (event?.payload?.sys?.id
          && catProdDiffState.rhs[productIndex].sys.updated_at
          && (new Date(event?.payload.sys.updated_at).valueOf() > new Date(catProdDiffState.rhs[productIndex].sys.updated_at as any).valueOf())
        ) catProdDiffState.rhs.splice(productIndex, 1, { ...catProdDiffState.rhs[productIndex], ...event?.payload })
      })
    })

    const storeUrl = computed((): string => {
      return services?.value?.find(s => s.id === props.catalogue?.service?.id)?.account?.value || ''
    })

    const isLinkedToShopify = computed((): boolean => {
      if (!props.catalogue.service) return false
      return props.catalogue.service.id?.toLowerCase().includes('shopify') === true
    })
    const filteredCatalogueProducts = computed(() => {
      const variantIds = Object.values(variants.value).flat()

      return catProdDiffState.rhs
        // filters out individual items that are shown as variants
        .filter(catalogue_product => !variantIds.includes(catalogue_product.id))
        .sort((x, y) => x?.sys?.draft ? -1 : (!x.sku ? 0 : x.sku - (y.sku || 0)))
    })

    const fetchCatalogueProducts = async () => {
      try {
        if (!props.catalogue.sys.id || !pageSize.value) return
        const whereClause = () => ({
          and: [
            { catalogue: props.catalogue.sys.id },
            filterValue.value === 'productWithMissingLink' ? { id: null } : undefined,
            filterValue.value === 'activeProducts' ? { active: true } : undefined,
            filterValue.value === 'syncErrors' ? { 'meta ? \'sync_error\'': true } : undefined,
            searchQuery.value?.length ? {
              or: [
                { ilike: ['title', searchQuery.value] },
                { ilike: [{ col: 'sku', cast: 'text' }, searchQuery.value] },
                {
                  inSubquery: ['id', {
                    select: ['__id'],
                    from: [ContentType.Product],
                    where: {
                      or: [
                        { ilike: ['title', searchQuery.value] },
                        { ilike: ['sku', searchQuery.value] },
                        { ean: { overlap: searchQuery.value?.split(','), type: 'varchar' } },
                        {
                          inSubquery: ['components[1]', {
                            select: ['__id'],
                            from: [ContentType.StockItem],
                            where: {
                              or: [
                                { ilike: ['title', searchQuery.value] },
                                { ilike: ['sku', searchQuery.value] },
                                { ean: { overlap: searchQuery.value?.split(','), type: 'varchar' } }
                              ]
                            }
                          }]
                        }
                      ]
                    }
                  }]
                },
              ]
            } : undefined,
          ].filter(Boolean)
        })
        const tempDiffState = { lhs: [] as CtCatalogueProduct[], rhs: [] as CtCatalogueProduct[] }
        // catalogue products for page
        await Promise.all([
          $directr.get(ContentType.CatalogueProduct, [{
            select: ['COUNT(*)'],
            where: whereClause(),
          }], { orm: true }).then(
            ({ items = [{ count: 0 }] as [{ count: number }] }) => totalCatProducts.value = items[0]?.count ?? 0
          ),
          $directr.get(ContentType.CatalogueProduct, [{
            select: ['catalogue_product.*'],
            where: whereClause(),
            orderBy: { sku: 'ASC' },
            offset: (currentPage.value - 1) * pageSize.value,
            limit: pageSize.value,
          }], { orm: true }).then(async ({ items = [] as CtCatalogueProduct[], sys }) => {
            items = items.map(item => ({
              ...item,
              sys: {
                ...(item?.sys || {}),
                editing: false,
                removing: false
              }
            }))
            tempDiffState.lhs = cloneDeep(items)
            tempDiffState.rhs = (items || [])
            // variant catalogue products
            await Promise.all([
              $directr.get(ContentType.CatalogueProduct, [{
                select: ['catalogue_product.*'],
                where: {
                  inSubquery: ['id', {
                    select: ['unnest(variants)'],
                    from: [ContentType.Product],
                    where: {
                      __id: { in: tempDiffState.lhs.map(item => item.id) }
                    },
                    orderBy: { sku: 'ASC' },
                  }
                  ]
                }
              }], { orm: true }).then(({ items = [] as CtCatalogueProduct[] }) => {
                tempDiffState.lhs = tempDiffState.lhs.concat(cloneDeep(items))
                tempDiffState.rhs = tempDiffState.rhs.concat(items)
              }),
              $directr.get(ContentType.Product, [{
                select: ['product.*'],
                where: {
                  or: [
                    { __id: { in: tempDiffState.lhs.map(item => item.id) } }
                  ]
                }
              }], { orm: true }).then(({ items = [] as CtProduct[] }) => {
                products.value = items
                dictionary.products = products.value.reduce((acc, prod) => {
                  acc[prod.sys.id] = prod
                  return acc
                }, {})
                variants.value = items.reduce((acc, product) => {
                  acc[product.sys.id] = product.variants?.filter(f => !!f && f !== 'NULL') || []
                  return acc
                }, {})
                const productIds = Object.values(variants.value).flat().filter(Boolean)
                if (!productIds.length) return {}
                const packingIds: Array<UUID> = items.map((product: CtProduct) => {
                  const components: Array<CtStockItem> = (product.components || []) as unknown as Array<CtStockItem>
                  return [product.fixed_fulfillment_tier].concat(components.map(c => (c.packing || []).map(pack => pack && ('sys' in pack) ? pack.sys.id : pack)).flat(1))
                })
                  .flat(2)
                  .filter(f => !!f)
                  .map((n: Entry | UUID | undefined) => typeof n === 'string' ? n : n?.sys?.id)
                  .filter(notEmpty)
                  .filter((f, i, a) => a.indexOf(f) === i)
                emit('packing-resolved', packingIds)
              })
            ])
            catProdDiffState.lhs = tempDiffState.lhs
            catProdDiffState.rhs = tempDiffState.rhs
          })
        ])
      } catch (e: any) {
        console.log(e)
      }
    }
    const resetAndFetch = () => {
      currentPage.value = 1
      totalCatProducts.value = 0
      void fetchCatalogueProducts()
    }

    const skuController = ref<SkuCtrl>(SkuController(props.catalogue))
    watch(() => props.catalogue, c => skuController.value = SkuController(c))
    //
    // filters
    //
    const filterValues = [
      { label: 'Everything', val: 'everything' },
      { label: 'Products with missing link', val: 'productWithMissingLink' },
      // TODO add filter options based on stock / inventory
      // { label: 'Available products in stock', val: 'availableProductsInStock' },
      // { label: 'Unavailable products', val: 'unavailableProducts' },
      { label: 'Active products', val: 'activeProducts' },
      { label: 'Products with sync errors', val: 'syncErrors' }
    ]
    const filterValue = ref('everything')
    const searchQuery = ref('')
    watch(() => filterValue.value, resetAndFetch)
    watch(() => searchQuery.value, debounce(resetAndFetch, 1000))
    //
    // pagination
    //
    const currentPage = ref(1)
    watch(() => currentPage.value, fetchCatalogueProducts)
    const totalCatProducts = ref(0)
    const pageCount = computed(
      () => totalCatProducts.value && pageSize.value ? Math.ceil(totalCatProducts.value / pageSize.value) : 0
    )
    const pageSize = ref<number | null>(null)
    watch(() => [props.catalogue, pageSize.value], resetAndFetch)
    const dummyCard = ref<InstanceType<typeof ProductCard> | null>(null)
    const showMoreProducts = ref<HTMLDivElement | null>(null)
    const calculatePageSize = () => {
      const collapsedProductCardHeight = dummyCard.value?.$el?.getBoundingClientRect().height
      const containerHeight = window.innerHeight - (filterableList.value?.parentEl?.getBoundingClientRect().height ?? 0) - (showMoreProducts.value?.getBoundingClientRect().height ?? 0)
      if (containerHeight === undefined || collapsedProductCardHeight === undefined) {
        return 9
      } else {
        const numVerticalCards = Math.floor(containerHeight / collapsedProductCardHeight)
        return numVerticalCards * 3
      }
    }
    watch(() => dummyCard.value, (value) => {
      if (value) {
        pageSize.value = calculatePageSize()
      }
    })
    const setPage = async (newPage) => {
      if (!editing.value) {
        currentPage.value = newPage
      } else {
        const discardChanges = await confirmDiscard()
        if (discardChanges) {
          editing.value = false
          currentPage.value = newPage
        }
      }
    }
    //
    // interface
    //
    const editing = ref(false)
    const cancelEditing = () => {
      catProdDiffState.rhs = cloneDeep(catProdDiffState.lhs)
      editing.value = false
    }
    const cancelProduct = (productSysId) => {
      const rIndex = catProdDiffState.rhs.findIndex(p => p.sys.id === productSysId)
      const lIndex = catProdDiffState.lhs.findIndex(p => p.sys.id === productSysId)
      if (lIndex !== -1) {
        catProdDiffState.rhs[rIndex] = cloneDeep(catProdDiffState.lhs[lIndex])
      }
      catProdDiffState.rhs[rIndex].sys.editing = false
    }
    const expanded = ref(false)
    const expandable = computed(() => filteredCatalogueProducts.value.length > 6)
    const filterableList = ref<InstanceType<typeof FilterableList> | null>(null)
    const toggleExpand = async () => {
      if (!expanded.value) {
        filterableList.value?.$el.scrollIntoView({ behavior: 'smooth' })
        await nextTick()
        expanded.value = !expanded.value
      } else {
        expanded.value = !expanded.value
      }
    }
    const expandedProducts = ref(new Set())
    const expandProduct = (product, expand) => {
      if (expand) {
        expandedProducts.value.add(product.sys.id)
      } else {
        expandedProducts.value.delete(product.sys.id)
      }
    }
    //
    // actions
    //
    const updateProduct = (updatedProduct) => {
      const index = catProdDiffState.rhs.findIndex(item => item.sys.id === updatedProduct.sys.id)
      if (index >= 0) {
        catProdDiffState.rhs[index] = updatedProduct
        if (updatedProduct.sys.editing && !editing.value) editing.value = true
      }
    }
    const updateVariant = (variant) => {
      // console.log(variant.sys.id, variant, catProdDiffState.rhs)
      catProdDiffState.rhs[catProdDiffState.rhs.findIndex(v => v.sys.id === variant.sys.id)] = { ...variant, sys: { ...(variant?.sys || {}), editing: true } }
    }
    const draft: ProductListHandlers<CtProduct, CtCatalogueProduct>['draft'] = (type, product, catalogue) => ({
      sys: { id: uuid(), draft: true, type },
      id: product.sys.id,
      catalogue: catalogue.sys.id as UUID,
      title: product.title,
      price: 0,
      active: true
    })
    const addVariant = (p) => {
      const d = draft(ContentType.CatalogueProduct, p, props.catalogue)
      catProdDiffState.rhs = catProdDiffState.rhs.concat([d])
    }
    const removeProduct = (product, remove) => {
      if (!editing.value) editing.value = true
      product.sys.removing = remove
    }
    const save = async () => {
      const newCatProducts = (await Promise.all(catProdDiffState.rhs.filter(cp => cp.sys.draft).map(async (cp) => {
        if (dictionary.products[cp.id]?.title === cp.title) delete cp.title
        const { items = [] } = await $directr.put(ContentType.CatalogueProduct, cp.sys?.id, cp)
        if (items.length) {
          return items[0]
        } else {
          // dirty fix for sku unique constraint violation
          const { items = [] } = await $directr.put(ContentType.CatalogueProduct, cp.sys?.id, cp)
          return items[0]
        }
      })))
      const updatedCatProducts = (await Promise.all(catProdDiffState.rhs.filter(cp => !cp.sys.draft && cp.sys.editing).map(async (cp) => {
        const { items = [] } = await $directr.put(ContentType.CatalogueProduct, cp.sys.id, cp)
        return items[0]
      })))
      const deletedCatProducts = (await Promise.all(catProdDiffState.rhs.filter(cp => !cp.sys.draft && cp.sys.removing).map(async (cp) => {
        const { items = [] } = await $directr.delete(ContentType.CatalogueProduct, cp.sys.id)
        return items[0]
      })))

      void fetchCatalogueProducts()
      editing.value = false
    }
    //
    // dialogs
    //
    const addingProducts = reactive({
      show: false,
      draftProductIds: computed(
        () => catProdDiffState.rhs.filter(cp => cp?.sys?.draft).map(cp => cp.id).filter(Boolean)
      ),
      addProducts: ({ selection }) => {
        const drafts: Array<CtCatalogueProduct> = selection.map((product: CtProduct) => draft(ContentType.CatalogueProduct, product, props.catalogue))
        selection.map(s => {
          dictionary.products[s.sys.id] = s
        })
        drafts.forEach(d => {
          if (!catProdDiffState.rhs.find(r => r.id === d.id)) catProdDiffState.rhs.push(d)
        })
        editing.value = true
      },
    })

    const addVariantsDialog = ref<InstanceType<typeof AddVariantsDialog> | null>(null)
    const addingVariants = reactive({
      catProdId: null as CtCatalogueProduct['sys']['id'] | null,
      prodId: computed(() => catProdDiffState.rhs.find(r => r.sys.id === addingVariants.catProdId)?.id || null),
      currentVariants: computed(() => (variants.value[addingVariants.prodId] || []).filter(v => catProdDiffState.rhs.find(r => r.id === v))),
      addVariants: ({ selection }) => {
        selection.map(s => {
          dictionary.products[s.sys.id] = s
        })
        const drafts: Array<CtCatalogueProduct> = selection
          .map((product: CtProduct) => draft(ContentType.CatalogueProduct, product, props.catalogue))
        products.value = products.value.concat(selection.filter(p => !products.value.find(p2 => p.sys.id === p2.sys.id)))
        drafts.forEach(d => {
          if (!catProdDiffState.rhs.find(r => r.id === d.id)) catProdDiffState.rhs.push(d)
        })
        editing.value = true
        addingVariants.catProdId = null
      }
    })
    watch(() => [props.catalogue, catProdDiffState.rhs], () => {
      addVariantsDialog.value?.refresh()
    })

    const linkProductDialog = ref<InstanceType<typeof LinkProductDialog> | null>(null)
    const linkingProduct = reactive({
      catProdId: null as CtCatalogueProduct['sys']['id'] | null,
      currentProducts: computed(() => catProdDiffState.rhs.map(cp => cp.id).filter(id => id !== null && id !== undefined)),
      linkProduct: ({ selection }) => {
        const catProdIndex = catProdDiffState.rhs.findIndex(r => r.sys.id === linkingProduct.catProdId)
        if (catProdIndex < 0) return
        catProdDiffState.rhs[catProdIndex].id = selection[0].sys.id
        catProdDiffState.rhs[catProdIndex].sys.editing = true
        editing.value = true
        linkingProduct.catProdId = null
      }
    })
    watch(() => [props.catalogue, catProdDiffState.rhs], () => linkProductDialog.value?.refresh())
    //
    // routing
    //
    const confirmDiscard = async (): Promise<boolean> => {
      return new Promise((resolve, reject) => {
        if (props.readonly) return resolve(true)
        return $quasar.dialog({
          title: 'Discard unsaved changes?',
          message: 'Do you want to leave and discard your unsaved changes?',
          persistent: true,
          cancel: {
            label: 'Go back',
            flat: true,
            color: 'black',
          },
          ok: {
            label: 'Discard',
            color: 'negative',
          },
        }).onOk(() => {
          resolve(true)
        }).onCancel(() => {
          resolve(false)
        })
      })
    }

    onBeforeRouteUpdate(async (to, from) => {
      if (editing.value) return await confirmDiscard()
      return true
    })

    onBeforeRouteLeave(async (to, from) => {
      if (editing.value) return await confirmDiscard()
      return true
    })

    return {
      // data
      filteredCatalogueProducts,
      products,
      catalogueVariants,
      skuController,
      // filters
      filterValues,
      filterValue,
      searchQuery,
      // pagination
      currentPage,
      pageCount,
      pageSize,
      setPage,
      // interface
      editing,
      isLinkedToShopify,
      storeUrl,
      cancelEditing,
      cancelProduct,
      expanded,
      expandable,
      toggleExpand,
      expandProduct,
      // actions
      updateProduct,
      updateVariant,
      addVariant,
      removeProduct,
      save,
      // dialogs
      addingProducts,
      addVariantsDialog,
      addingVariants,
      linkProductDialog,
      linkingProduct,
      // refs
      filterableList,
      showMoreProducts,
      dummyCard,
    }
  }
})
